import React, { useState, useRef } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import NoteIcon from '@material-ui/icons/Note';
import ProductNoteDialog from './ProductNoteDialog';
import { IconButton, Popover, Typography, Box, CircularProgress } from '@material-ui/core';
import axios from 'axios';
import { useSelector } from 'react-redux';
import envConfig from '../../../bin/env.config';

const useStyles = makeStyles((theme) => ({
  yellowIcon: {
    color: '#ffc107', // Màu vàng
    '&:hover': {
      backgroundColor: 'rgba(255, 193, 7, 0.1)',
    },
  },
  popoverContent: {
    padding: theme.spacing(2),
    maxWidth: 400,
    maxHeight: 500,
    overflowY: 'auto',
    backgroundColor: '#fdf5bf',
    fontSize: 12,
  },
  noteItem: {
    marginBottom: 12,
    borderBottom: '1px solid #ffc107',
  },
  noteHeader: {
    display: 'flex',
    // gap: 8,
    justifyContent: 'space-between',
    background: '#ffc107',
    padding: '4px 8px',
  },
  noteMeta: {
    display: 'flex',
    alignItems: 'left',
  },
  noteText: {
    // fontSize: 14,
    margin: '4px 0 8px 0',
  },
}));

export default function ButtonProductNote({ productId }) {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [notes, setNotes] = useState([]);
  const [fetchingNoteHistory, setFetchingNoteHistory] = useState(false);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const token = useSelector((state) => state.session.token);
  const hoverTimeoutRef = useRef(null);
  const popoverTimeoutRef = useRef(null);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const fetchNotesHistory = async () => {
    if (!productId || loadingNotes || fetchingNoteHistory) return;
    
    setLoadingNotes(true);
    setFetchingNoteHistory(true);
    try {
      const res = await axios.post(
        `${envConfig.API_ROOT}${envConfig.API_VERSION}/company-report-tool/product-note-history`,
        {
          company_report_tool_product_id: productId,
        },
        {
          headers: { Authorization: token },
        }
      );
      const notes = res.data?.data || [];
      setNotes(notes);
    } catch (err) {
      setNotes([]);
    } finally {
      setLoadingNotes(false);
    }
  };

  const handleMouseEnter = (event) => {
    // Load notes history khi hover
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    setAnchorEl(event.currentTarget);
    hoverTimeoutRef.current = setTimeout(() => {
      fetchNotesHistory();
      setPopoverOpen(true);
    }, 300); // Delay 300ms để tránh load quá nhiều khi di chuột nhanh
  };

  const handleMouseLeave = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    // Delay đóng popover để user có thể di chuột vào popover
    if (popoverTimeoutRef.current) {
      clearTimeout(popoverTimeoutRef.current);
    }
    popoverTimeoutRef.current = setTimeout(() => {
      setPopoverOpen(false);
      setAnchorEl(null);
      // Reset để có thể fetch lại khi hover lần sau
      if (!loadingNotes) {
        setFetchingNoteHistory(false);
      }
    }, 200);
  };

  const handlePopoverMouseEnter = () => {
    // Giữ popover mở khi hover vào popover
    if (popoverTimeoutRef.current) {
      clearTimeout(popoverTimeoutRef.current);
    }
  };

  const handlePopoverMouseLeave = () => {
    // Đóng popover khi rời khỏi popover
    setPopoverOpen(false);
    setAnchorEl(null);
    // Reset để có thể fetch lại khi hover lần sau
    if (!loadingNotes) {
      setFetchingNoteHistory(false);
    }
  };

  return (
    <>
      <IconButton        
        className={classes.yellowIcon}
        onClick={handleOpen}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        alt="Give a note for this product"
        title="Give a note for this product"
      >
        <NoteIcon />
      </IconButton>
      <Popover
        open={popoverOpen}
        anchorEl={anchorEl}
        onClose={handlePopoverMouseLeave}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        onMouseEnter={handlePopoverMouseEnter}
        onMouseLeave={handlePopoverMouseLeave}
        disableRestoreFocus
      >
        <Box className={classes.popoverContent}>
          {loadingNotes ? (
            <Box display="flex" justifyContent="center" alignItems="center" p={2}>
              <CircularProgress size={24} />
            </Box>
          ) : notes.length === 0 ? (
            <Typography variant="body2" color="textSecondary" style={{ textAlign: 'center', padding: '16px' }}>
              No notes yet
            </Typography>
          ) : (
            notes.map((note) => (
              <div key={note.id} className={classes.noteItem}>
                <div className={classes.noteHeader}>
                  <div className={classes.noteMeta}>
                    <span style={{ margin: '4px 12px 0px 0px' }}>{note.edited_at}</span>
                    <span style={{ margin: '4px 0 0 0px' }}>{note.edited_by}</span>
                  </div>
                </div>
                <div className={classes.noteText}>{note.note}</div>
              </div>
            ))
          )}
        </Box>
      </Popover>
      <ProductNoteDialog
        open={open}
        onClose={handleClose}
        productId={productId}
      />
    </>
  );
}
